/* eslint-disable no-underscore-dangle */
import { useFormatBreederContactInfo } from '@application/api';
import {
  isFieldsAvailableInLanguage,
  translatedFieldWithFallback,
  useRouter,
  useSlideshow,
} from '@application/utils';
import type { BreederGetBreederQuery } from '@gql/graphql';
import type { CardItemBreedSeoData } from '@presentation/components';
import type { BreederPageProps } from '@presentation/pages';
import type { LocationDescriptor } from 'history';
import { useMemo } from 'react';
import { useFormatLitters } from '../../LitterListPageController/api/useFormatLitters';
import { useSeo } from '../useSeo';

type Breeder = NonNullable<BreederGetBreederQuery['findManyDogBreeder']>[number];

// TODO clean this
export const useFormatBreeder = (breeder?: Breeder, filteredBreeds?: Breeder['breederBreeds']) => {
  const { navigateToRoute, language, routeLocationDescriptor, infoFromCurrentPath } = useRouter();

  const breederImages = useMemo(() => {
    return (breeder?.imgs ?? []).filter((img) => !['cover', 'default'].includes(img.type));
  }, [breeder?.imgs]);

  const { formattedLitters } = useFormatLitters(breeder?.litters);
  const { breedNames, titleMultipleNames } = useSeo(breeder, filteredBreeds);
  const slideshow = useSlideshow(
    breederImages.map((image) => ({
      src: image.medium ?? image.large ?? '',
      thumbnail: image.small ?? '',
    })),
  );
  const { contactInfo } = useFormatBreederContactInfo(breeder);

  const formattedBreeds = useMemo((): CardItemBreedSeoData[] | undefined => {
    if (!filteredBreeds?.length) return;
    return filteredBreeds.map(({ dogBreed }) => {
      let found = dogBreed?.translations?.find((x) => x.language === language);
      if (!found) {
        found = dogBreed?.translations?.[0];
      }
      return {
        breedName: found?.name ?? dogBreed.name,
        image: dogBreed.imgs?.[0]?.small,
        link: routeLocationDescriptor('breed', { params: [dogBreed.translations?.[0]?.url ?? ''] }),
      } as CardItemBreedSeoData;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breeder?.breederBreeds, language, navigateToRoute, routeLocationDescriptor]);

  const backLink = useMemo((): LocationDescriptor => {
    return routeLocationDescriptor('breederList', {
      search: infoFromCurrentPath?.state?.returnSearch ?? {},
      params: infoFromCurrentPath?.state?.returnBreed ?? [],
    });
  }, [
    infoFromCurrentPath?.state?.returnBreed,
    infoFromCurrentPath?.state?.returnSearch,
    routeLocationDescriptor,
  ]);

  const formattedBreeder: Partial<BreederPageProps> = useMemo(() => {
    if (!breeder) return {};

    return {
      ...(slideshow.items.length ? { slideshow } : {}),
      contactInfo,
      breedNames,
      whatsappNumber: breeder.whatsappNumber,
      header: {
        title: titleMultipleNames,
        backLink,
      },
      content: {
        description: translatedFieldWithFallback(language, breeder?.translations, 'description'),
        puppiesLivingPlace: translatedFieldWithFallback(
          language,
          breeder?.translations,
          'puppiesLivingPlace',
        ),
        livingPlace: translatedFieldWithFallback(
          language,
          breeder?.translations,
          'dogsLivingPlace',
        ),
        parentSelection: translatedFieldWithFallback(
          language,
          breeder?.translations,
          'breederDogsSelection',
        ),
        puppiesWork: translatedFieldWithFallback(
          language,
          breeder?.translations,
          'puppiesTraining',
        ),
      },

      litters: formattedLitters,
      breeds: formattedBreeds,
    } as Partial<BreederPageProps>;
  }, [
    breeder,
    slideshow,
    contactInfo,
    breedNames,
    titleMultipleNames,
    backLink,
    language,
    formattedLitters,
    formattedBreeds,
  ]);

  const imagesToPreload = useMemo(() => {
    const firstSlideshowItem = formattedBreeder?.slideshow?.items[0];
    if (firstSlideshowItem && !firstSlideshowItem.isVideo) {
      return [firstSlideshowItem.src];
    }
    return [];
  }, [formattedBreeder?.slideshow?.items]);

  const contentOriginalLanguage = useMemo(() => {
    const currentLangAvailable = isFieldsAvailableInLanguage(language, breeder?.translations, [
      'description',
      'puppiesLivingPlace',
      'dogsLivingPlace',
      'breederDogsSelection',
      'puppiesTraining',
    ]);
    if (currentLangAvailable) return language;
    return breeder?.account.language;
  }, [breeder?.account.language, breeder?.translations, language]);

  return { formattedBreeder, imagesToPreload, contentOriginalLanguage };
};
